import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '../firebase';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Notification sound — two-tone chime, ~1.5 seconds
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();

    function playTone(freq, startTime, duration, vol) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(vol, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    }

    const t = ctx.currentTime;
    // First ting
    playTone(830, t, 0.6, 0.4);
    playTone(1245, t, 0.6, 0.2);
    // Second ting (higher, delayed)
    playTone(1108, t + 0.2, 0.8, 0.35);
    playTone(1661, t + 0.2, 0.8, 0.15);
    // Third soft ting
    playTone(1320, t + 0.5, 1.0, 0.2);

    // Close context after sound finishes
    setTimeout(() => ctx.close().catch(() => {}), 2000);
  } catch {}
}

// Inject CSS animation for the gradient glow
const STYLE_ID = 'direct-chat-bubble-styles';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes chatUnreadGlow {
      0% { box-shadow: 0 0 6px 2px rgba(37,211,102,0.5), 0 0 12px 4px rgba(37,211,102,0.2); }
      50% { box-shadow: 0 0 12px 6px rgba(37,211,102,0.7), 0 0 24px 8px rgba(37,211,102,0.3); }
      100% { box-shadow: 0 0 6px 2px rgba(37,211,102,0.5), 0 0 12px 4px rgba(37,211,102,0.2); }
    }
    @keyframes chatBadgePulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
    @keyframes chatToastSlideIn {
      0% { opacity: 0; transform: translateY(10px) scale(0.95); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
  `;
  document.head.appendChild(style);
}

export default function DirectChatBubble() {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [adminName, setAdminName] = useState('Nitin Potdar');
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [toast, setToast] = useState(null);
  const endRef = useRef(null);
  const lastCountRef = useRef(-1);
  const hasInteracted = useRef(false);
  const toastTimer = useRef(null);
  const isOpenRef = useRef(false);

  const userId = user?.id;

  // Track user interaction so we can play sound (browsers require gesture before audio)
  useEffect(() => {
    const handler = () => { hasInteracted.current = true; };
    const events = ['click', 'touchstart', 'keydown', 'scroll'];
    events.forEach((e) => window.addEventListener(e, handler, { once: true, passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, handler));
  }, []);

  // Keep ref in sync
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  const loadMessages = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.get(`/direct-chat/${userId}/messages`);
      const msgs = data.messages || [];
      setMessages(msgs);

      const adminMsgsList = msgs.filter((m) => m.sender === 'admin');
      const adminMsgs = adminMsgsList.length;

      // First load — just set the baseline, don't toast
      if (lastCountRef.current === -1) {
        lastCountRef.current = adminMsgs;
        return;
      }

      if (adminMsgs > lastCountRef.current) {
        if (!isOpenRef.current) {
          setUnread(adminMsgs - lastCountRef.current);
          // Show toast with latest admin message
          const latestMsg = adminMsgsList[adminMsgsList.length - 1];
          if (latestMsg) {
            setToast(latestMsg.text);
            clearTimeout(toastTimer.current);
            toastTimer.current = setTimeout(() => setToast(null), 5000);
            playNotificationSound();
          }
        } else {
          lastCountRef.current = adminMsgs;
        }
      }
    } catch {}
  }, [userId]);

  const loadStatus = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.get(`/direct-chat/${userId}/status`);
      const isActive = data.active === true;
      setActive(isActive);
      if (data.adminName) setAdminName(data.adminName);
      if (!isActive) {
        setIsOpen(false);
        setMessages([]);
        setUnread(0);
        lastCountRef.current = -1;
      }
    } catch {}
  }, [userId]);

  // Initial load
  useEffect(() => {
    loadStatus();
    loadMessages();
  }, [loadStatus, loadMessages]);

  // RTDB listener + polling fallback
  useEffect(() => {
    if (!userId) return;
    let unsub;
    try {
      const chatRef = ref(rtdb, `directChats/${userId}`);
      unsub = onValue(chatRef, (snap) => {
        const data = snap.val();
        if (data) {
          const isActive = data.active === true;
          setActive(isActive);
          if (data.adminName) setAdminName(data.adminName);
          if (!isActive) {
            setIsOpen(false);
            setMessages([]);
            setUnread(0);
            lastCountRef.current = -1;
            return;
          }
        }
        loadMessages();
      }, () => {});
    } catch {}

    const poll = setInterval(() => {
      loadStatus();
      loadMessages();
    }, 5000);

    return () => {
      if (unsub) unsub();
      clearInterval(poll);
    };
  }, [userId, loadMessages, loadStatus]);

  // Mark as read and clear toast when panel is open
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      lastCountRef.current = messages.filter((m) => m.sender === 'admin').length;
      setUnread(0);
      setToast(null);
      clearTimeout(toastTimer.current);
    }
  }, [isOpen, messages]);

  useEffect(() => {
    if (isOpen) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/direct-chat/${userId}/message`, { text });
      setMessages((prev) => [...prev, data]);
      setInput('');
    } catch {}
    setSending(false);
  };

  if (!active || !userId) return null;

  const hasUnread = unread > 0 && !isOpen;

  return (
    <>
      {/* Chat panel */}
      {isOpen && (
        <div style={s.panel}>
          <div style={s.panelHeader}>
            <div style={s.panelHeaderLeft}>
              <div style={s.panelAvatar}>NP</div>
              <div>
                <p style={s.panelName}>{adminName}</p>
                <p style={s.panelSub}>Legal Mentor</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} style={s.panelClose}>&times;</button>
          </div>

          <div style={s.panelMessages}>
            {messages.length === 0 && (
              <p style={s.emptyMsg}>Start of your conversation with {adminName}</p>
            )}
            {messages.map((msg) => (
              <div key={msg._id || msg.id} style={{ ...s.msgRow, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={msg.sender === 'user' ? s.msgUser : s.msgAdmin}>
                  <p style={s.msgText}>{msg.text}</p>
                  <span style={s.msgTime}>
                    {new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div style={s.panelInput}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Type a message..."
              style={s.input}
              autoFocus
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              style={{ ...s.sendBtn, opacity: input.trim() && !sending ? 1 : 0.4 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* Toast preview */}
      {toast && !isOpen && (
        <div style={s.toast} onClick={() => { setIsOpen(true); setUnread(0); setToast(null); lastCountRef.current = messages.filter(m => m.sender === 'admin').length; }}>
          <div style={s.toastHeader}>
            <span style={s.toastName}>{adminName}</span>
            <button onClick={(e) => { e.stopPropagation(); setToast(null); }} style={s.toastClose}>&times;</button>
          </div>
          <p style={s.toastText}>{toast.length > 120 ? toast.slice(0, 120) + '...' : toast}</p>
        </div>
      )}

      {/* Floating bubble */}
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) { setUnread(0); lastCountRef.current = messages.filter(m => m.sender === 'admin').length; } }}
        style={{
          ...s.bubble,
          ...(hasUnread ? { animation: 'chatUnreadGlow 1.5s ease-in-out infinite' } : {}),
        }}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        )}
        {hasUnread && (
          <span style={s.unreadBadge}>
            {unread}
          </span>
        )}
      </button>
    </>
  );
}

const s = {
  bubble: {
    position: 'fixed', bottom: '20px', right: '20px', zIndex: 9990,
    width: '56px', height: '56px', borderRadius: '50%',
    background: '#25D366', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(37,211,102,0.4)',
    transition: 'transform 0.2s',
  },
  unreadBadge: {
    position: 'absolute', top: '-6px', right: '-6px',
    minWidth: '24px', height: '24px', borderRadius: '12px',
    background: '#e53e3e', color: '#fff', fontSize: '12px', fontWeight: '800',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '2.5px solid #fff',
    padding: '0 4px',
    animation: 'chatBadgePulse 1.5s ease-in-out infinite',
  },
  panel: {
    position: 'fixed', bottom: '86px', right: '20px', zIndex: 9991,
    width: '360px', maxWidth: 'calc(100vw - 32px)',
    height: '480px', maxHeight: 'calc(100vh - 120px)',
    background: '#fff', borderRadius: '20px',
    boxShadow: '0 8px 40px rgba(12,35,64,0.2)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', border: '1px solid #e2e8f0',
  },
  panelHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', background: '#0c2340', color: '#fff', flexShrink: 0,
  },
  panelHeaderLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  panelAvatar: {
    width: '36px', height: '36px', borderRadius: '50%',
    background: 'rgba(200,155,60,0.25)', color: '#c89b3c',
    fontSize: '13px', fontWeight: '800',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Playfair Display', Georgia, serif",
  },
  panelName: { fontSize: '14px', fontWeight: '700', margin: 0, color: '#fff' },
  panelSub: { fontSize: '11px', color: 'rgba(255,255,255,0.6)', margin: 0 },
  panelClose: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
    fontSize: '24px', cursor: 'pointer', padding: '0 4px', lineHeight: 1,
  },
  panelMessages: {
    flex: 1, overflowY: 'auto', padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: '8px',
    background: '#f7f8fa',
  },
  emptyMsg: { textAlign: 'center', color: '#a0aec0', fontSize: '13px', padding: '30px 10px' },
  msgRow: { display: 'flex' },
  msgAdmin: {
    background: '#0c2340', color: '#fff',
    borderRadius: '14px 14px 14px 4px', padding: '10px 14px', maxWidth: '80%',
  },
  msgUser: {
    background: '#fff', color: '#1a202c',
    border: '1px solid #e2e8f0',
    borderRadius: '14px 14px 4px 14px', padding: '10px 14px', maxWidth: '80%',
  },
  msgText: { fontSize: '13px', lineHeight: '1.5', margin: 0, wordBreak: 'break-word' },
  msgTime: { fontSize: '10px', opacity: 0.5, marginTop: '4px', display: 'block', textAlign: 'right' },
  panelInput: {
    display: 'flex', gap: '8px', padding: '12px 16px',
    borderTop: '1px solid #e2e8f0', background: '#fff', flexShrink: 0,
  },
  input: {
    flex: 1, padding: '10px 14px', borderRadius: '10px',
    border: '1px solid #e2e8f0', fontSize: '13px',
    fontFamily: "'DM Sans', sans-serif", outline: 'none', background: '#f7f8fa',
  },
  sendBtn: {
    background: '#0c2340', color: '#fff', border: 'none',
    width: '40px', height: '40px', borderRadius: '10px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  toast: {
    position: 'fixed', bottom: '86px', right: '20px', zIndex: 9992,
    width: '280px', maxWidth: 'calc(100vw - 96px)',
    background: '#fff', borderRadius: '16px',
    boxShadow: '0 6px 28px rgba(12,35,64,0.18)',
    border: '1px solid #e2e8f0',
    padding: '12px 16px', cursor: 'pointer',
    animation: 'chatToastSlideIn 0.3s ease-out',
  },
  toastHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '4px',
  },
  toastName: {
    fontSize: '12px', fontWeight: '800', color: '#0c2340',
    fontFamily: "'DM Sans', sans-serif",
  },
  toastClose: {
    background: 'none', border: 'none', fontSize: '18px',
    color: '#a0aec0', cursor: 'pointer', padding: '0 2px', lineHeight: 1,
  },
  toastText: {
    fontSize: '13px', color: '#4a5568', lineHeight: '1.45', margin: 0,
    wordBreak: 'break-word',
  },
};
